// frontend/src/components/forms/ClientForm.tsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Client, Vet, clientsAPI, referenceDataAPI } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import CustomSelectMenuItem from '../common/CustomSelectMenuItem';
import { CancelFormButton, SaveFormButton } from '../common/Buttons';
import { useGlobalToast } from '../../App';
import { useEnterNavigation } from '../../hooks/useEnterNavigation';
import {
  FormWrapper,
  FormContent,
  StickyFooter,
  StickyButtonContainer,
  FormFieldRow,
  FieldLabelContainer,
  FieldLabel,
  FieldInputContainer,
  StyledTextField,
  StyledMultilineTextField,
  ResponsiveFormGrid,
  FieldLabelContainerMultiline,
  StyledSwitch,
  SimilarityDialog,
  TopSpacerRow,
  BottomSpacerRow,
} from './shared';
import DangerZoneRow from './shared/DangerZoneRow';
import DeleteConfirmDialog from './shared/modal/DeleteConfirmDialog';
import {
  formValidators,
  isFormValid,
  FormErrors,
  scrollToFirstError,
} from '../../utils/validationHelpers';
import { findFirstSimilarVet } from '../../utils/vetSimilarityHelper';
import { findFirstSimilarClient } from '../../utils/clientSimilarityHelper';
import LoadingSpinner from '../common/LoadingSpinner';

interface ClientFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  vetId: string;
  otherVetName: string;
  otherVetPhone: string;
  idNumber: string;
  alternateContact: string;
  alternateContactOther: string;
  notes: string;
  showWarning: boolean;
}

type ClientFormErrors = FormErrors<ClientFormData>;

interface ClientFormProps {
  client?: Client;
  onSuccess: (client: Client) => void;
  onCancel: () => void;
  title?: string;
  showWarning?: boolean;
  alternateRowColors?: boolean;
  scrollToFirstError?: boolean;
}

const ClientForm: React.FC<ClientFormProps> = ({
  client,
  onSuccess,
  onCancel,
  title,
  showWarning = false,
  scrollToFirstError: enableScrollToError = true,
}) => {
  const theme = useTheme();
  const { showSuccess, showError } = useGlobalToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ClientFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    vetId: '',
    otherVetName: '',
    otherVetPhone: '',
    idNumber: '',
    alternateContact: '',
    alternateContactOther: '',
    notes: '',
    showWarning: false,
  });

  const [errors, setErrors] = useState<ClientFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [availableVets, setAvailableVets] = useState<Vet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);

  const [showVetSimilarityDialog, setShowVetSimilarityDialog] = useState(false);
  const [similarVet, setSimilarVet] = useState<Vet | null>(null);
  // Vet id chosen via "Use Existing", read before the state update lands.
  const pendingVetIdRef = useRef<number | null>(null);

  const [showClientSimilarityDialog, setShowClientSimilarityDialog] =
    useState(false);
  const [similarClient, setSimilarClient] = useState<Client | null>(null);
  const [allClients, setAllClients] = useState<Client[]>([]);

  const [originalFirstName, setOriginalFirstName] = useState('');
  const [originalLastName, setOriginalLastName] = useState('');

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isEditMode = Boolean(client);
  const showOtherVetFields = formData.vetId === 'other';

  // Enter-as-Tab order, including the conditional new-vet rows.
  const fieldOrder = useMemo(() => {
    const order: string[] = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'address',
      'vetId',
    ];
    if (showOtherVetFields) {
      order.push('otherVetName', 'otherVetPhone');
    }
    order.push(
      'idNumber',
      'alternateContact',
      'alternateContactOther',
      'showWarning',
      'notes',
      'submit'
    );
    return order;
  }, [showOtherVetFields]);

  const { registerRef, getObjectRef, getKeyHandler, focusFirst } =
    useEnterNavigation(fieldOrder);

  // Refs for scrollToFirstError.
  const fieldRefs = useMemo(() => {
    const map = new Map<keyof ClientFormData, React.RefObject<HTMLElement>>();
    (
      [
        'firstName',
        'lastName',
        'email',
        'phone',
        'otherVetName',
        'otherVetPhone',
        'notes',
      ] as const
    ).forEach((name) => map.set(name, getObjectRef(name)));
    return map;
  }, [getObjectRef]);

  useEffect(() => {
    if (client) {
      const firstName = client.firstName || '';
      const lastName = client.lastName || '';

      setFormData({
        firstName,
        lastName,
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        vetId: client.vetId ? client.vetId.toString() : '',
        otherVetName: '',
        otherVetPhone: '',
        idNumber: client.idNumber || '',
        alternateContact: client.alternateContact || '',
        alternateContactOther: client.alternateContactOther || '',
        notes: client.notes || '',
        showWarning: client.showWarning || false,
      });

      setRowVersion(client.rowVersionString ?? client.rowVersion);
      setOriginalFirstName(firstName);
      setOriginalLastName(lastName);
    }
  }, [client]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const vets = await referenceDataAPI.getVets();
        setAvailableVets(vets);

        const clients = await clientsAPI.getAll();
        setAllClients(clients);
      } catch (error) {
        showError();
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [showError]);

  // Autofocus First Name in create mode once loading finishes.
  useEffect(() => {
    if (isEditMode || isLoading) return;
    const timer = setTimeout(() => {
      focusFirst();
    }, 100);
    return () => clearTimeout(timer);
  }, [isEditMode, isLoading, focusFirst]);

  const validateForm = (): boolean => {
    const newErrors: ClientFormErrors = {};

    const firstNameError = formValidators.client.firstName(formData.firstName);
    if (firstNameError) newErrors.firstName = firstNameError;

    const lastNameError = formValidators.client.lastName(formData.lastName);
    if (lastNameError) newErrors.lastName = lastNameError;

    const emailError = formValidators.client.email(formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = formValidators.client.phone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    if (showOtherVetFields) {
      if (!formData.otherVetName.trim()) {
        newErrors.otherVetName = 'Vet name is required';
      }
      if (!formData.otherVetPhone.trim()) {
        newErrors.otherVetPhone = 'Vet phone is required';
      }
    }

    const notesError = formValidators.client.notes(
      formData.notes,
      formData.showWarning
    );
    if (notesError) newErrors.notes = notesError;

    setErrors(newErrors);

    const hasErrors = !isFormValid(newErrors);
    if (hasErrors && enableScrollToError) {
      scrollToFirstError(newErrors, fieldRefs, 120);
    }

    return !hasErrors;
  };

  const handleInputChange = (
    field: keyof ClientFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field as keyof ClientFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    if (field === 'showWarning' && !value && errors.notes) {
      setErrors((prev) => ({ ...prev, notes: undefined }));
    }
  };

  const handleVetChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      vetId: value,
      otherVetName: '',
      otherVetPhone: '',
    }));

    setErrors((prev) => ({
      ...prev,
      otherVetName: undefined,
      otherVetPhone: undefined,
    }));
  };

  const handleUseExistingVet = () => {
    if (similarVet) {
      pendingVetIdRef.current = similarVet.id;
      setFormData((prev) => ({
        ...prev,
        vetId: similarVet.id.toString(),
        otherVetName: '',
        otherVetPhone: '',
      }));

      setErrors((prev) => ({
        ...prev,
        otherVetName: undefined,
        otherVetPhone: undefined,
      }));
    }

    setShowVetSimilarityDialog(false);
    setSimilarVet(null);
    checkClientSimilarity();
  };

  const handleCreateNewVet = () => {
    setShowVetSimilarityDialog(false);
    setSimilarVet(null);
    checkClientSimilarity();
  };

  const handleUseExistingClient = () => {
    if (isEditMode && similarClient) {
      setFormData((prev) => ({
        ...prev,
        firstName: originalFirstName,
        lastName: originalLastName,
      }));

      setErrors((prev) => ({
        ...prev,
        firstName: undefined,
        lastName: undefined,
      }));
    } else if (!isEditMode && similarClient) {
      navigate(`/clients/${similarClient.id}`);
      return;
    }

    setShowClientSimilarityDialog(false);
    setSimilarClient(null);
  };

  const handleCreateNewClient = () => {
    setShowClientSimilarityDialog(false);
    setSimilarClient(null);
    proceedWithSubmit(false);
  };

  const checkClientSimilarity = () => {
    const nameHasChanged =
      isEditMode &&
      (formData.firstName.trim() !== originalFirstName ||
        formData.lastName.trim() !== originalLastName);

    const shouldCheckSimilarity = !isEditMode || nameHasChanged;

    if (
      shouldCheckSimilarity &&
      formData.firstName.trim() &&
      formData.lastName.trim()
    ) {
      const similar = findFirstSimilarClient(
        formData.firstName,
        formData.lastName,
        allClients,
        client?.id
      );

      if (similar) {
        setSimilarClient(similar);
        setShowClientSimilarityDialog(true);
        return;
      }
    }

    proceedWithSubmit(false);
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    if (showOtherVetFields && formData.otherVetName.trim()) {
      const similar = findFirstSimilarVet(formData.otherVetName, availableVets);

      if (similar) {
        setSimilarVet(similar);
        setShowVetSimilarityDialog(true);
        return;
      }
    }

    checkClientSimilarity();
  };

  const proceedWithSubmit = async (forceCreate: boolean) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let finalVetId: number | undefined = undefined;

      if (pendingVetIdRef.current !== null) {
        finalVetId = pendingVetIdRef.current;
        pendingVetIdRef.current = null;
      } else if (showOtherVetFields && formData.otherVetName.trim()) {
        try {
          const vetResponse = await referenceDataAPI.createVet({
            name: formData.otherVetName.trim(),
            phone: formData.otherVetPhone.trim(),
          });

          finalVetId = vetResponse.id;

          const updatedVets = await referenceDataAPI.getVets();
          setAvailableVets(updatedVets);
        } catch (vetError: any) {
          setSubmitError(
            vetError.response?.data?.message ||
              'Failed to create vet. Please try again.'
          );
          setIsSubmitting(false);
          return;
        }
      } else if (formData.vetId && formData.vetId !== 'other') {
        finalVetId = parseInt(formData.vetId);
      }

      const clientData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        vetId: finalVetId,
        idNumber: formData.idNumber.trim(),
        alternateContact: formData.alternateContact.trim(),
        alternateContactOther: formData.alternateContactOther.trim(),
        notes: formData.notes.trim(),
        showWarning: formData.showWarning,
      };

      let savedClient: Client;
      if (isEditMode && client) {
        savedClient = await clientsAPI.update(client.id, {
          id: client.id,
          ...clientData,
          rowVersionString: rowVersion,
        });
      } else {
        savedClient = await clientsAPI.create(clientData);
      }

      showSuccess();
      onSuccess(savedClient);
    } catch (error: any) {
      setSubmitError(
        error.response?.data?.message ||
          'Failed to save client. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!client) return;
    setIsDeleting(true);
    try {
      await clientsAPI.delete(client.id);
      showSuccess();
      navigate('/clients');
    } catch {
      showError('Failed to delete client');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <FormWrapper>
        <LoadingSpinner
          message="Loading client form..."
          size={60}
          minHeight="300px"
        />
      </FormWrapper>
    );
  }

  return (
    <FormWrapper>
      <FormContent>
        <ResponsiveFormGrid
          breakpointPadding={{
            default: '0.25rem',
            medium: '0.1rem',
            large: '0.1rem',
          }}
          container
          spacing={0}
        >
          <TopSpacerRow datesSelected={true} />

          {/* First Name */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-firstName-label">
                First Name <span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-firstName"
                inputProps={{ 'aria-labelledby': 'client-firstName-label' }}
                fullWidth
                value={formData.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('firstName', e.target.value)
                }
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                disabled={isSubmitting}
                inputRef={registerRef('firstName')}
                onKeyDown={getKeyHandler('firstName')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Last Name */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-lastName-label">
                Last Name <span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-lastName"
                inputProps={{ 'aria-labelledby': 'client-lastName-label' }}
                fullWidth
                value={formData.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('lastName', e.target.value)
                }
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                disabled={isSubmitting}
                inputRef={registerRef('lastName')}
                onKeyDown={getKeyHandler('lastName')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Email */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-email-label">
                Email <span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-email"
                inputProps={{ 'aria-labelledby': 'client-email-label' }}
                fullWidth
                type="email"
                value={formData.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('email', e.target.value)
                }
                error={Boolean(errors.email)}
                helperText={errors.email}
                disabled={isSubmitting}
                inputRef={registerRef('email')}
                onKeyDown={getKeyHandler('email')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Phone */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-phone-label">
                Phone <span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-phone"
                inputProps={{ 'aria-labelledby': 'client-phone-label' }}
                fullWidth
                value={formData.phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('phone', e.target.value)
                }
                error={Boolean(errors.phone)}
                helperText={errors.phone}
                disabled={isSubmitting}
                inputRef={registerRef('phone')}
                onKeyDown={getKeyHandler('phone')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Address */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-address-label">Address</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-address"
                inputProps={{ 'aria-labelledby': 'client-address-label' }}
                fullWidth
                value={formData.address}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('address', e.target.value)
                }
                disabled={isSubmitting}
                inputRef={registerRef('address')}
                onKeyDown={getKeyHandler('address')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Vet Selection */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>Vet</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                <CustomSelect
                  value={formData.vetId}
                  onChange={handleVetChange}
                  ariaLabel="Vet"
                  disabled={isSubmitting}
                  width="100%"
                  maxWidth="600px"
                  ref={registerRef('vetId')}
                  onKeyDown={getKeyHandler('vetId')}
                >
                  <CustomSelectMenuItem value="">None</CustomSelectMenuItem>
                  {availableVets.map((vet) => (
                    <CustomSelectMenuItem
                      key={vet.id}
                      value={vet.id.toString()}
                    >
                      {vet.name}
                    </CustomSelectMenuItem>
                  ))}
                  <CustomSelectMenuItem value="other">
                    + Add New Vet
                  </CustomSelectMenuItem>
                </CustomSelect>
              )}
            </FieldInputContainer>
          </FormFieldRow>

          {/* Other Vet Name (conditional) */}
          {showOtherVetFields && (
            <FormFieldRow>
              <FieldLabelContainer>
                <FieldLabel id="client-otherVetName-label">
                  Vet Name <span className="required">*</span>
                </FieldLabel>
              </FieldLabelContainer>
              <FieldInputContainer>
                <StyledTextField
                  id="client-otherVetName"
                  inputProps={{
                    'aria-labelledby': 'client-otherVetName-label',
                  }}
                  fullWidth
                  value={formData.otherVetName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('otherVetName', e.target.value)
                  }
                  error={Boolean(errors.otherVetName)}
                  helperText={errors.otherVetName}
                  disabled={isSubmitting}
                  inputRef={registerRef('otherVetName')}
                  onKeyDown={getKeyHandler('otherVetName')}
                />
              </FieldInputContainer>
            </FormFieldRow>
          )}

          {/* Other Vet Phone (conditional) */}
          {showOtherVetFields && (
            <FormFieldRow>
              <FieldLabelContainer>
                <FieldLabel id="client-otherVetPhone-label">
                  Vet Phone <span className="required">*</span>
                </FieldLabel>
              </FieldLabelContainer>
              <FieldInputContainer>
                <StyledTextField
                  id="client-otherVetPhone"
                  inputProps={{
                    'aria-labelledby': 'client-otherVetPhone-label',
                  }}
                  fullWidth
                  value={formData.otherVetPhone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('otherVetPhone', e.target.value)
                  }
                  error={Boolean(errors.otherVetPhone)}
                  helperText={errors.otherVetPhone}
                  disabled={isSubmitting}
                  inputRef={registerRef('otherVetPhone')}
                  onKeyDown={getKeyHandler('otherVetPhone')}
                />
              </FieldInputContainer>
            </FormFieldRow>
          )}

          {/* ID Number */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-idNumber-label">ID Number</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-idNumber"
                inputProps={{ 'aria-labelledby': 'client-idNumber-label' }}
                fullWidth
                value={formData.idNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('idNumber', e.target.value)
                }
                disabled={isSubmitting}
                inputRef={registerRef('idNumber')}
                onKeyDown={getKeyHandler('idNumber')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Alternate Contact */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-alternateContact-label">
                Alt. Contact
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-alternateContact"
                inputProps={{
                  'aria-labelledby': 'client-alternateContact-label',
                }}
                fullWidth
                value={formData.alternateContact}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('alternateContact', e.target.value)
                }
                disabled={isSubmitting}
                inputRef={registerRef('alternateContact')}
                onKeyDown={getKeyHandler('alternateContact')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Alternate Contact Other */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-alternateContactOther-label">
                Alt. Contact Other
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="client-alternateContactOther"
                inputProps={{
                  'aria-labelledby': 'client-alternateContactOther-label',
                }}
                fullWidth
                value={formData.alternateContactOther}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('alternateContactOther', e.target.value)
                }
                disabled={isSubmitting}
                inputRef={registerRef('alternateContactOther')}
                onKeyDown={getKeyHandler('alternateContactOther')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Show Warning */}
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="client-showWarning-label">
                Show Warning
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box
                display="flex"
                alignItems="center"
                onKeyDown={getKeyHandler('showWarning')}
              >
                <StyledSwitch
                  id="client-showWarning"
                  inputProps={{ 'aria-labelledby': 'client-showWarning-label' }}
                  checked={formData.showWarning}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('showWarning', e.target.checked)
                  }
                  disabled={isSubmitting}
                  showLabels={true}
                  trueLabel="Yes"
                  falseLabel="No"
                  inputRef={registerRef('showWarning')}
                />
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          {/* Notes field */}
          <FormFieldRow>
            <FieldLabelContainerMultiline>
              <FieldLabel id="client-notes-label">
                Notes
                {formData.showWarning && <span className="required">*</span>}
              </FieldLabel>
            </FieldLabelContainerMultiline>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledMultilineTextField
                id="client-notes"
                inputProps={{ 'aria-labelledby': 'client-notes-label' }}
                fullWidth
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleInputChange('notes', e.target.value)
                }
                error={Boolean(errors.notes)}
                helperText={errors.notes}
                disabled={isSubmitting}
                inputRef={registerRef('notes')}
                onKeyDown={getKeyHandler('notes')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Save error */}
          {submitError && (
            <FormFieldRow>
              <FieldLabelContainer>
                <FieldLabel></FieldLabel>
              </FieldLabelContainer>
              <FieldInputContainer paddingLeft={'.5rem'}>
                <Box
                  sx={{
                    padding: '12px',
                    backgroundColor: '#ffebee',
                    border: `1px solid ${theme.palette.secondary.main}`,
                    borderRadius: '4px',
                    color: theme.palette.secondary.main,
                    fontSize: '2rem',
                    lineHeight: '1.5',
                    maxWidth: '600px',
                  }}
                >
                  <strong>{isEditMode ? 'Save' : 'Create'} Failed:</strong>{' '}
                  {submitError}
                </Box>
              </FieldInputContainer>
            </FormFieldRow>
          )}

          {isEditMode && client && (
            <DangerZoneRow
              label="Delete Client"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isSubmitting}
            />
          )}

          <BottomSpacerRow minHeight="17rem" />
        </ResponsiveFormGrid>
      </FormContent>

      <StickyFooter>
        <StickyButtonContainer>
          <CancelFormButton
            onClick={onCancel}
            minWidth="278px"
            sx={{
              minWidth: { xs: '175px', sm: '278px' },
            }}
          />
          <SaveFormButton
            onClick={handleSubmit}
            isSubmitting={isSubmitting}
            text={isEditMode ? 'Save' : 'Create'}
            minWidth="278px"
            sx={{
              minWidth: { xs: '165px', sm: '278px' },
            }}
            ref={registerRef('submit')}
            onKeyDown={getKeyHandler('submit')}
          />
        </StickyButtonContainer>
      </StickyFooter>

      {/* Vet Similarity Dialog */}
      <SimilarityDialog
        open={showVetSimilarityDialog}
        entityType="vet"
        similarEntity={similarVet}
        isEditMode={false}
        onUseExisting={handleUseExistingVet}
        onCreateNew={handleCreateNewVet}
        onClose={() => {
          pendingVetIdRef.current = null;
          setShowVetSimilarityDialog(false);
        }}
      />

      {/* Client Similarity Dialog */}
      <SimilarityDialog
        open={showClientSimilarityDialog}
        entityType="client"
        similarEntity={similarClient}
        isEditMode={isEditMode}
        onUseExisting={handleUseExistingClient}
        onCreateNew={handleCreateNewClient}
        onClose={() => setShowClientSimilarityDialog(false)}
      />

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteClient}
        isDeleting={isDeleting}
        title="Delete Client"
        message={
          <>
            This will delete{' '}
            <strong>
              {client?.firstName} {client?.lastName}
            </strong>{' '}
            and all associated pets, bookings, invoices, and payments. This
            action cannot be undone from here.
          </>
        }
      />
    </FormWrapper>
  );
};

export default ClientForm;
